import React from "react";
import { Link } from "react-router-dom"; 
import Certif1 from '../../Asset/certification-qualisap.jpg';
import Certif2 from '../../Asset/logo_caphandeo.jpg';
import Certif3 from '../../Asset/logo_services_personne.jpg';
import ImageHAS from '../../Asset/HAS.png'; 
import ImageCertif from '../../Asset/image8.png';
import { Helmet } from 'react-helmet';
import { useColorMode, Box } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaCalendarAlt, FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaAward } from 'react-icons/fa';

function Certif() {
    const { colorMode } = useColorMode();
    const isDark = colorMode === 'dark';

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.2 },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 40 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
    };

    const sectionVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    return (
        <Box className={isDark ? 'bg-[#0a0f16] min-h-screen' : 'bg-[#FAFCFF] min-h-screen'}>
            <Helmet>
                <title>Certifications & Agréments — Hetep Iaout Services</title>
                <meta name="description" content="Hetep Iaout Services est certifié Cap'Handéo 2030 et Qualisap, agréé par l'État. Découvrez nos engagements qualité pour votre prise en charge à domicile." />
                <link rel="canonical" href="https://www.hisvie.com/agrements/" />
            </Helmet>

            <div className="w-full">
                
                {/* --- TITRE PRINCIPAL --- */}
                <motion.section
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-center pt-32 pb-16 px-6"
                >
                    <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border mb-8 text-[10px] font-bold uppercase tracking-widest ${isDark ? 'bg-blue-900/30 border-blue-800 text-blue-300' : 'bg-blue-50 border-blue-100 text-blue-800'}`}>
                        <FaAward /> Garanties Officielles
                    </div>
                    
                    <h1 className={`text-5xl md:text-7xl font-black mb-8 tracking-tighter leading-tight ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                        Nos <span className="text-blue-600 font-serif italic font-light">Certifications.</span>
                    </h1>
                    <p className={`text-lg md:text-xl max-w-3xl mx-auto leading-relaxed font-light ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        L'excellence de Hetep Iaout Services est validée par des organismes indépendants, garantissant la conformité de nos interventions aux normes les plus strictes.
                    </p>
                    <div className="w-24 h-1 bg-blue-600 mx-auto mt-10 rounded-full"></div>
                </motion.section>

                {/* --- SECTIONS DÉTAILLÉES --- */}
                <motion.section 
                    className="max-w-6xl mx-auto px-6 pb-24"
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.1 }}
                >
                    <div className="space-y-32">
                        
                        {/* Cap'Handéo */}
                        <motion.div variants={itemVariants} className="flex flex-col lg:flex-row items-center gap-16">
                            <div className="w-full lg:w-1/2 space-y-6">
                                <span className="text-blue-600 font-bold tracking-widest uppercase text-xs block">Label d'excellence</span>
                                <h2 className={`text-3xl md:text-5xl font-black leading-tight ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                                    Certification <br /> <span className="text-blue-600 font-serif italic font-light">Cap'Handéo</span>
                                </h2>
                                <p className={`text-lg font-light leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                    Garantie d'un accompagnement sur-mesure fondé sur des référentiels rédigés par des personnes en situation de handicap.
                                </p>
                                <div className="flex items-center gap-3 font-bold text-blue-600 bg-blue-50 dark:bg-blue-900/20 px-6 py-3 rounded-xl w-fit">
                                    <FaCalendarAlt /> Renouvelée jusqu'en 2030
                                </div>

                                {/* Périmètre de certification — requis par Cap'Handéo */}
                                <div className={`rounded-2xl border p-5 space-y-3 ${isDark ? 'bg-amber-900/10 border-amber-700/30' : 'bg-amber-50 border-amber-200'}`}>
                                    <div className="flex items-center gap-2">
                                        <FaMapMarkerAlt className="text-amber-500 shrink-0" />
                                        <span className={`text-xs font-black uppercase tracking-widest ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
                                            Périmètre de certification
                                        </span>
                                    </div>
                                    <p className={`text-sm font-light leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                        La certification Cap'Handéo Services à la personne couvre exclusivement les agences de <strong>La Courneuve</strong> et <strong>Saint-Denis (93)</strong>.
                                    </p>
                                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                                        Les agences de Bois-Colombes, Lille, Paris, Nangis et Domont ne sont pas incluses dans ce périmètre de certification.
                                    </p>
                                </div>
                            </div>
                            <div className="w-full lg:w-1/2 rounded-[2.5rem] overflow-hidden shadow-2xl border border-white/10">
                                <iframe
                                    className="w-full h-64 sm:h-80"
                                    src="https://www.youtube.com/embed/V4UzoFVjio0"
                                    title="Certification Cap'Handéo"
                                    frameBorder="0"
                                    allowFullScreen
                                />
                            </div>
                        </motion.div>

                        {/* HAS */}
                        <motion.div variants={itemVariants} className="relative group">
                            <div className={`relative overflow-hidden rounded-[3rem] shadow-2xl border transition-all duration-500 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
                                <div className="flex flex-col lg:flex-row items-stretch">
                                    <div className="p-10 md:p-16 lg:w-2/3 space-y-8">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-blue-600/10 flex items-center justify-center text-blue-600">
                                                <FaCheckCircle size={20} />
                                            </div>
                                            <span className="font-black uppercase tracking-widest text-[11px] opacity-60">Référentiel National</span>
                                        </div>
                                        <h2 className={`text-3xl md:text-5xl font-black leading-tight ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                                            Haute Autorité <br className="hidden md:block"/> de Santé (HAS)
                                        </h2>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-10">
                                            <div className={`p-6 rounded-2xl border transition-all ${isDark ? 'bg-white/5 border-white/5' : 'bg-gray-50 border-gray-100'}`}>
                                                <div className="flex items-center gap-3 mb-3 font-bold text-sm">
                                                    <FaMapMarkerAlt className="text-blue-600" /> Bureau de Paris
                                                </div>
                                                <span className="px-3 py-1 rounded-full bg-green-500/10 text-green-500 text-[10px] font-black uppercase border border-green-500/20">Satisfaisant</span>
                                            </div>
                                            <div className={`p-6 rounded-2xl border transition-all ${isDark ? 'bg-white/5 border-white/5' : 'bg-gray-50 border-gray-100'}`}>
                                                <div className="flex items-center gap-3 mb-3 font-bold text-sm">
                                                    <FaCalendarAlt className="text-blue-600" /> Bureau de Saint-Denis
                                                </div>
                                                <span className="px-3 py-1 rounded-full bg-blue-500/10 text-blue-500 text-[10px] font-black uppercase border border-blue-500/20">S1 2027</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className={`lg:w-1/3 flex items-center justify-center p-12 transition-colors ${isDark ? 'bg-white/5' : 'bg-blue-50/30'}`}>
                                        <motion.div whileHover={{ scale: 1.05 }} className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100">
                                            <img src={ImageHAS} alt="Logo HAS" className="w-full h-auto object-contain" />
                                        </motion.div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        {/* Qualisap */}
                        <motion.div variants={itemVariants} className="flex flex-col lg:flex-row-reverse items-center gap-16">
                            <div className="w-full lg:w-1/2 space-y-6">
                                <span className="text-blue-600 font-bold tracking-widest uppercase text-xs block">Engagement Qualité</span>
                                <h2 className={`text-3xl md:text-5xl font-black leading-tight ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                                    Certification <br /> <span className="text-blue-600 font-serif italic font-light">Qualisap v4</span>
                                </h2>
                                <p className={`text-lg font-light leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                    Garantit que l'humain reste au centre de chaque prestation, audité régulièrement par des instances indépendantes.
                                </p>
                            </div>
                            <div className="w-full lg:w-1/2">
                                <img src={ImageCertif} alt="Certification Qualisap" className="w-full h-auto rounded-[2.5rem] shadow-2xl border border-white/10" />
                            </div>
                        </motion.div>
                    </div>
                </motion.section>

                {/* --- SECTION CONFIANCE & LOGOS --- */}
                <motion.section 
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    className={`py-32 px-6 ${isDark ? 'bg-gray-900 border-t border-gray-800' : 'bg-[#1B365D]'}`}
                >
                    <div className="max-w-6xl mx-auto text-center">
                        <h2 className="text-4xl md:text-6xl font-black mb-6 text-white tracking-tighter">Pourquoi nous <br/> faire confiance ?</h2>
                        <p className="text-lg md:text-xl mb-20 text-gray-200 max-w-3xl mx-auto font-light opacity-90">
                            Notre agence est multicertifiée pour vous offrir un cadre d'intervention sécurisé et respectueux.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-24">
                            {[
                                { img: Certif2, title: "Label Cap'Handéo", validity: "Expertise Handicap", link: "https://www.handeo.fr" },
                                { img: Certif1, title: "Qualisap v4", validity: "Qualité Service", link: "https://www.servicesalapersonne.gouv.fr" },
                                { img: Certif3, title: "Agrément ANSP", validity: "État Français", link: "https://www.servicesalapersonne.gouv.fr" }
                            ].map((certif, index) => (
                                <motion.a
                                    key={index}
                                    href={certif.link} target="_blank" rel="noopener noreferrer"
                                    whileHover={{ y: -10 }}
                                    className={`flex flex-col items-center justify-center p-10 rounded-[2.5rem] shadow-xl transition-all border border-transparent ${isDark ? 'bg-gray-700 hover:border-blue-500/30' : 'bg-white/10 backdrop-blur-md border-white/10'}`}
                                >
                                    <div className="bg-white p-4 rounded-2xl shadow-xl mb-6">
                                        <img src={certif.img} alt={certif.title} className="h-20 object-contain" />
                                    </div>
                                    <span className={`font-bold text-xl text-center mb-2 text-white`}>
                                        {certif.title}
                                    </span>
                                    <span className="text-blue-400 text-[10px] font-black uppercase tracking-widest">{certif.validity}</span>
                                </motion.a>
                            ))}
                        </div>

                        <Link to="/NosRessources">
                            <motion.button 
                                whileHover={{ scale: 1.05 }}
                                className="bg-white text-[#1B365D] font-black py-5 px-12 rounded-2xl shadow-2xl hover:bg-blue-50 transition-all mb-32"
                            >
                                Voir Nos Ressources
                            </motion.button>
                        </Link>

                        {/* --- CTA FINAL FUSIONNÉ --- */}
                        <motion.div 
                            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={sectionVariants}
                            className="pt-10"
                        >
                            <div className={`relative overflow-hidden rounded-[3rem] p-10 md:p-16 flex flex-col md:flex-row items-center text-center md:text-left gap-10 border shadow-2xl ${isDark ? 'bg-[#050914] border-gray-800' : 'bg-white border-white/20'}`}>
                                <div className="absolute -right-20 -bottom-20 w-80 h-80 bg-blue-600/10 rounded-full blur-3xl"></div>
                                <div className="flex-1 z-10 space-y-4">
                                    <h2 className={`text-3xl md:text-5xl font-black tracking-tight leading-tight ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                                        Une question sur nos <br />
                                        <span className="text-blue-600 font-serif italic font-light">certifications ?</span>
                                    </h2>
                                    <p className={`text-lg font-light ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        Notre équipe vous accompagne pour mobiliser vos droits et acceptons le règlement par <strong>CESU préfinancé</strong>.
                                    </p>
                                </div>
                                <div className="flex flex-col sm:flex-row w-full md:w-auto gap-4 z-10 shrink-0">
                                    <Link to="/contact" className="w-full sm:w-auto">
                                        <button className="flex items-center justify-center gap-3 bg-blue-600 text-white px-10 py-5 rounded-2xl font-bold hover:bg-blue-700 transition-all w-full shadow-xl shadow-blue-600/30">
                                            <FaPhoneAlt size={16} /> Nous contacter
                                        </button>
                                    </Link>
                                    <a href="mailto:his@donnerlavieauxannees.com" className={`flex items-center justify-center gap-3 px-10 py-5 rounded-2xl font-bold transition-all border w-full sm:w-auto ${isDark ? 'bg-gray-800 text-gray-300 border-gray-700 hover:bg-gray-700' : 'bg-gray-50 text-[#1B365D] border-gray-200 hover:bg-gray-200'}`}>
                                        <FaEnvelope size={16} /> Par Email
                                    </a>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </motion.section>
            </div>
        </Box>
    );
}

export default Certif;